import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import SiteVisitDataSection from '../../components/gyms/SiteVisitDataSection'
import PerformanceTab from '../../components/gyms/PerformanceTab'
import ProposalSection from '../../components/gyms/ProposalSection'
import HandoverSection from '../../components/gyms/HandoverSection'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format } from 'date-fns'

const GymDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const [gym, setGym] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editingNotes, setEditingNotes] = useState(false)

  const isBDExecutive = user?.role?.level === 4

  useEffect(() => {
    fetchGymDetails()
  }, [id])

  const fetchGymDetails = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/api/gyms/${id}`)
      setGym(response.data.gym)
    } catch (error) {
      console.error('Error fetching gym details:', error)
      if (error.response?.status === 404) {
        toast.error('Gym not found')
        navigate('/gyms')
      } else if (error.response?.status === 403) {
        toast.error('Access denied: You can only view gyms assigned to you')
        navigate('/gyms')
      } else {
        toast.error('Failed to load gym details')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleNotesUpdate = async (notes) => {
    try {
      await axios.put(`/api/gyms/${id}`, { internalNotes: notes })
      setGym(prev => ({ ...prev, internalNotes: notes }))
      setEditingNotes(false)
      toast.success('Notes updated successfully')
    } catch (error) {
      console.error('Error updating notes:', error)
      toast.error('Failed to update notes')
    }
  }

  const handleDeleteGym = async () => {
    if (!window.confirm(`Are you sure you want to delete "${gym.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      await axios.delete(`/api/gyms/${id}`)
      toast.success(`Gym "${gym.name}" deleted successfully`)
      navigate('/gyms')
    } catch (error) {
      console.error('Error deleting gym:', error)
      const message = error.response?.data?.error || 'Failed to delete gym'
      toast.error(message)
    }
  }

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-blue-100 text-blue-800',
      'Proposal Sent': 'bg-yellow-100 text-yellow-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-purple-100 text-purple-800',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800'
  }

  const getStageProgress = (stage) => {
    const stages = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Gym Onboard']
    const index = stages.indexOf(stage)
    return index >= 0 ? ((index + 1) / stages.length) * 100 : 0
  }

  const canUserModifyGym = () => {
    if (!gym) return false
    
    // Head of Acquisition restrictions
    if (user?.role?.level === 3) {
      // Head of Acquisition cannot edit gyms beyond Facility Work stage
      const restrictedStages = ['Post Reki Approved', 'Gym Onboard']
      if (restrictedStages.includes(gym.stage)) {
        return false
      }
    }
    
    if (isBDExecutive) {
      return gym.assignedUserId === user.id
    }
    return hasPermission(3) // Head of Acquisition and above
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!gym) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">🏋️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Gym not found</h3>
        <Link to="/gyms" className="btn-primary">
          Back to Gyms
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{gym.name}</h1>
            <span className={`badge ${getStageColor(gym.stage)}`}>
              {gym.stage}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            Detailed gym information and progress tracking
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {canUserModifyGym() ? (
            <Link
              to={`/gyms/${gym.id}/edit`}
              className="btn-primary"
            >
              Edit Gym
            </Link>
          ) : user?.role?.level === 3 && ['Post Reki Approved', 'Gym Onboard'].includes(gym.stage) ? (
            <div className="relative group">
              <button
                disabled
                className="btn-primary opacity-50 cursor-not-allowed"
                title="Head of Acquisition cannot edit gyms beyond Facility Work stage"
              >
                Edit Gym
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                Head of Acquisition cannot edit gyms beyond Facility Work stage
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          ) : null}
          
          {canUserModifyGym() && (
            <button
              onClick={handleDeleteGym}
              className="btn-danger"
              title="Delete gym"
            >
              Delete Gym
            </button>
          )}
          
          <Link
            to="/gyms"
            className="btn-secondary"
          >
            Back to Gyms
          </Link>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="card">
        <div className="card-body">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress Overview</h2>
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Current Stage: {gym.stage}</span>
              <span>{Math.round(getStageProgress(gym.stage))}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-wtf-red h-3 rounded-full transition-all duration-300"
                style={{ width: `${getStageProgress(gym.stage)}%` }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Created:</span>
              <div className="text-gray-600">{format(new Date(gym.createdAt), 'MMM dd, yyyy')}</div>
            </div>
            
            {gym.launch_date && (
              <div>
                <span className="font-medium text-gray-700">Target Launch:</span>
                <div className={`${
                  new Date(gym.launch_date) < new Date() 
                    ? 'text-red-600' 
                    : 'text-green-600'
                }`}>
                  {format(new Date(gym.launch_date), 'MMM dd, yyyy')}
                </div>
              </div>
            )}
            
            {gym.assignedUser && (
              <div>
                <span className="font-medium text-gray-700">Assigned Manager:</span>
                <div className="text-gray-600">
                  {gym.assignedUser.name}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <p className="mt-1 text-sm text-gray-900">{gym.address}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <p className="mt-1 text-sm text-gray-900">{gym.location}</p>
                </div>
                
                {gym.state && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <p className="mt-1 text-sm text-gray-900">{gym.state}</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Pin Code</label>
                <p className="mt-1 text-sm text-gray-900">{gym.pinCode}</p>
              </div>
              
              {(gym.gps_lat && gym.gps_lng) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Coordinates</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {gym.gps_lat}, {gym.gps_lng}
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {gym.phone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <p className="mt-1 text-sm text-gray-900">{gym.phone}</p>
                </div>
              )}
              
              {gym.contact_person && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Owner Name</label>
                  <p className="mt-1 text-sm text-gray-900">{gym.contact_person}</p>
                </div>
              )}
              
              {(gym.google_maps_url || (gym.gps_lat && gym.gps_lng)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Google Maps Location</label>
                  <a
                    href={gym.google_maps_url || `https://maps.google.com/?q=${encodeURIComponent(gym.name || '')}+${gym.gps_lat},${gym.gps_lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm text-wtf-red hover:text-red-700"
                  >
                    View on Google Maps
                  </a>
                </div>
              )}
              
              {gym.placeType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Place Type</label>
                  <p className="mt-1 text-sm text-gray-900 capitalize">{gym.placeType}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SiteVisitDataSection gym={gym} />

      <PerformanceTab gymId={gym.id} canEdit={user?.role?.level <= 3} />
      <ProposalSection gym={gym} canEdit={user?.role?.level <= 3} onUpdate={fetchGymDetails} />
      <HandoverSection gym={gym} onUpdate={fetchGymDetails} />

      {/* Agreement Document Upload */}
      {gym.stage === 'Agreement Signed' && (
        <div className="bg-white rounded-lg border p-6 mt-4">
          <h3 className="font-semibold text-gray-900 mb-3">Signed Agreement Document</h3>
          {gym.agreementDocUrl ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">Agreement uploaded</p>
                <a href={gym.agreementDocUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View Document</a>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-3">Upload the signed agreement document (PDF or image)</p>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Agreement
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={async (e) => {
                    if (!e.target.files[0]) return
                    const formData = new FormData()
                    formData.append('agreement', e.target.files[0])
                    try {
                      await axios.post(`/api/gyms/${gym.id}/upload-agreement`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      })
                      toast.success('Agreement uploaded successfully')
                      window.location.reload()
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Upload failed')
                    }
                  }}
                />
              </label>
            </div>
          )}
        </div>
      )}

      {/* Internal Notes for BD Executive */}
      {canUserModifyGym() && (
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Internal Notes</h2>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-sm text-wtf-red hover:text-red-700"
                >
                  Edit Notes
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {editingNotes ? (
              <div className="space-y-4">
                <textarea
                  defaultValue={gym.internalNotes || ''}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                  rows={5}
                  placeholder="Add internal notes for tracking..."
                  id="internal-notes"
                />
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      const notes = document.getElementById('internal-notes').value
                      handleNotesUpdate(notes)
                    }}
                    className="btn-primary"
                  >
                    Save Notes
                  </button>
                  <button
                    onClick={() => setEditingNotes(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                {gym.internalNotes || 'No internal notes added yet. Click Edit Notes to add notes for tracking.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        </div>
        <div className="card-body">
          <div className="flex flex-wrap gap-3">
            <Link
              to="/kanban"
              className="btn-secondary"
            >
              View in Kanban
            </Link>

            <button
              onClick={() => navigate(`/pre-work-checklist/${gym.id}`)}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 hover:border-blue-500 hover:text-blue-700 transition-colors"
            >
              Pre-Work Checklist
            </button>
            
            {gym.gmbUrl && (
              <a
                href={gym.gmbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                Open Google Maps
              </a>
            )}
            
            {gym.phoneNumber && (
              <a
                href={`tel:${gym.phoneNumber}`}
                className="btn-secondary"
              >
                Call Gym
              </a>
            )}
            
            {gym.ownerContact && (
              <a
                href={`tel:${gym.ownerContact}`}
                className="btn-secondary"
              >
                Call Owner
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GymDetail 
